import { PanelTitle } from './ui/MetricPanel';
import { useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { usePollingResource } from '../hooks/usePollingResource';
import { fmtKst, fmtNumber } from '../lib/format';
import ResourceNotice from './ResourceNotice';
import { StatusChip } from './ui/Primitives';
import OutputQualityNotice from './OutputQualityNotice';
import type { OutputQuality } from '../api/intelligence';

interface CallRecord {
  call_id: string; ts: string; task: string; model: string; provider: string;
  status: string; total_tokens: number; cost_usd: number; latency_ms: number;
  response_content: string | null; error_message: string | null;
  prompt_messages: { role: string; content: string }[];
  grounding_review?: { call_id: string; accepted: boolean };
  parameters: Record<string, unknown>; output_quality?: OutputQuality;
}
export default function LLMHistoryPanel() {
  const [params, setParams] = useSearchParams();
  const task = params.get('task') ?? '';
  const status = ['SUCCESS', 'FALLBACK', 'FAILED'].includes(params.get('call_status') ?? '') ? params.get('call_status')! : '';
  const offset = Math.max(0, Number(params.get('offset')) || 0);
  const callId = params.get('call_id');
  const query = new URLSearchParams({ limit: '20', offset: String(offset) });
  if (task) query.set('task', task);
  if (status) query.set('status', status);
  const history = usePollingResource<{ records: CallRecord[]; total: number }>(`/llm/history?${query}`, 60000);
  const selected = usePollingResource<{ record: CallRecord }>(callId ? `/llm/history/${encodeURIComponent(callId)}` : null, 60000);
  const change = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    if (key !== 'offset' && key !== 'call_id') next.delete('offset');
    setParams(next);
  };
  const record = selected.data?.record;
  useEffect(() => {
    if (callId && record?.call_id === callId) {
      document.getElementById('llm-selected')?.scrollIntoView();
    }
  }, [callId, record?.call_id]);
  return <section className="card" id="llm-history">
    <div className="card-title-row"><PanelTitle>추론 호출 이력 · 입력과 응답</PanelTitle>
      <button className="btn-secondary" onClick={() => { history.refresh(); selected.refresh(); }}>이력 새로고침</button></div>
    <p className="muted-note">이력은 전체 저장 기간에서 조회합니다. 위 기간별 작업 통계와 범위가 다릅니다. SUCCESS는 원래 호출 상태이며 답변 완결성·정확도와 다릅니다.</p>
    <form className="filter-row" onSubmit={e => { e.preventDefault(); change('task', String(new FormData(e.currentTarget).get('task') ?? '').trim()); }}>
      <label>작업명<input type="text" name="task" key={task} defaultValue={task} placeholder="NEWS_SCORING_BATCH"/></label>
      <label>호출 결과<select value={status} onChange={e => change('call_status', e.target.value)}><option value="">전체</option><option>SUCCESS</option><option>FALLBACK</option><option>FAILED</option></select></label>
      <button className="btn-secondary" type="submit">작업 조회</button>
    </form>
    <ResourceNotice {...history}/>
    <div className="table-wrap"><table className="intelligence-table"><thead><tr><th>시각·작업</th><th>모델·결과</th><th>토큰·비용</th><th>응답 근거</th></tr></thead>
      <tbody>{history.data?.records.map(r => <tr key={r.call_id}>
        <td data-label="시각·작업">{fmtKst(r.ts)} KST<br />{r.task}</td>
        <td data-label="모델·결과">{r.model}<br /><StatusChip tone={r.output_quality?.state === 'ready' ? 'ok' : 'warn'}>{r.status}</StatusChip><OutputQualityNotice value={r.output_quality} grounding={r.grounding_review}/></td>
        <td data-label="토큰·비용">{fmtNumber(r.total_tokens)} 토큰<br />${fmtNumber(r.cost_usd, { digits: 6 })} · {r.latency_ms}ms</td>
        <td data-label="응답 근거"><button className="btn-secondary" onClick={() => change('call_id', r.call_id)}>입력·응답 보기</button>
          {r.error_message && <p className="research-alert">{r.error_message}</p>}</td>
      </tr>)}</tbody></table></div>
    {history.data?.records.length === 0 && <p>필터에 맞는 저장 호출 없음</p>}
    <div className="btn-row"><button className="btn-secondary" disabled={offset === 0} onClick={() => change('offset', String(Math.max(0, offset - 20)))}>이전</button>
      <span>{offset + 1}부터 · 총 {history.data?.total ?? '—'}회</span>
      <button className="btn-secondary" disabled={!history.data || offset + 20 >= history.data.total} onClick={() => change('offset', String(offset + 20))}>다음</button></div>
    {callId && <section className="analysis-reasoning" id="llm-selected" aria-label="선택한 추론 근거">
      <h4>선택한 추론 · {callId}</h4><ResourceNotice {...selected}/>
      {record && <><p>{record.task} · {record.model} · {record.provider} · {record.status}<br />{fmtKst(record.ts)} KST</p>
        {record.error_message && <p className="research-alert">{record.error_message}</p>}
        <OutputQualityNotice value={record.output_quality} grounding={record.grounding_review}/><h5>저장 응답</h5><p className="saved-analysis-text">{record.response_content ?? '저장된 응답 없음'}</p>
        <details><summary>원본 프롬프트·파라미터</summary>
          {record.prompt_messages?.map((m, i) => <div key={i}><h5>{m.role}</h5><pre className="code-block">{m.content}</pre></div>)}
          <pre className="code-block">{JSON.stringify(record.parameters, null, 2)}</pre>
        </details></>}
    </section>}
  </section>;
}
